import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { safeFormat } from '@/lib/safeDate';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Calendar, ExternalLink, Skull, CheckCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ReactivationCardProps {
  contact: {
    id: string;
    reactivation_status?: string;
    reactivation_cycle_count?: number;
    next_reactivation_at?: string;
    contact_lifecycle_status?: string;
  };
}

const STATUS_CONFIG = {
  scheduled: {
    label: 'Agendada',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock,
  },
  in_progress: {
    label: 'Em Progresso',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: RefreshCw,
  },
  completed: {
    label: 'Reativado com Sucesso',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle,
  },
  no_response: {
    label: 'Sem Resposta (Dead)',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: Skull,
  },
};

export function ReactivationCard({ contact }: ReactivationCardProps) {
  const navigate = useNavigate();
  const [activeDeal, setActiveDeal] = useState<{ id: string; stage: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const status = contact.reactivation_status;
  const isDead = contact.contact_lifecycle_status === 'dead';
  const cycle = contact.reactivation_cycle_count || 0;
  const maxCycles = 6;

  // Se não há status de reativação e não é dead, não mostrar o card
  if (!status && !isDead) return null;

  const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.scheduled;
  const StatusIcon = config.icon;

  // Buscar deal de reativação ativo
  useEffect(() => {
    const fetchActiveDeal = async () => {
      if (!contact.id || (status !== 'in_progress' && status !== 'scheduled')) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('deals')
          .select('id, stage')
          .eq('contact_id', contact.id)
          .eq('is_reactivation', true)
          .not('stage', 'in', '("won","lost")')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          setActiveDeal(data);
        }
      } catch (err) {
        console.error('Error fetching reactivation deal:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchActiveDeal();
  }, [contact.id, status]);

  const handleViewDeal = () => {
    if (activeDeal) {
      navigate(createPageUrl('DealDetail') + `?id=${activeDeal.id}`);
    }
  };

  return (
    <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-orange-600" />
          Reativação
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Badge */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Status:</span>
          <Badge variant="outline" className={`flex items-center gap-1 ${isDead ? STATUS_CONFIG.no_response.color : config.color}`}>
            {isDead ? <Skull className="h-3 w-3" /> : <StatusIcon className="h-3 w-3" />}
            {isDead ? STATUS_CONFIG.no_response.label : config.label}
          </Badge>
        </div>

        {/* Cycle Progress */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-slate-500">Ciclo</span>
            <span className="text-sm font-medium text-slate-700">{cycle} de {maxCycles}</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all ${isDead ? 'bg-red-500' : 'bg-orange-500'}`}
              style={{ width: `${(cycle / maxCycles) * 100}%` }}
            />
          </div>
        </div>

        {/* Next Reactivation Date */}
        {contact.next_reactivation_at && status === 'scheduled' && (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-slate-400" />
            <span className="text-slate-500">Próxima tentativa:</span>
            <span className="font-medium text-slate-700">
              {safeFormat(new Date(contact.next_reactivation_at), "dd 'de' MMM, yyyy", { locale: ptBR })}
            </span>
          </div>
        )}

        {/* View Deal Button */}
        {activeDeal && (status === 'in_progress' || status === 'scheduled') && (
          <Button
            variant="outline"
            size="sm"
            className="w-full border-orange-300 text-orange-700 hover:bg-orange-100"
            onClick={handleViewDeal}
            disabled={loading}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Ver Deal de Reativação
          </Button>
        )}

        {/* Dead Message */}
        {isDead && (
          <p className="text-xs text-red-600 bg-red-50 p-2 rounded">
            Este contato não respondeu após {maxCycles} tentativas de reativação e foi marcado como inativo permanentemente.
          </p>
        )}
      </CardContent>
    </Card>
  );
}